import { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

interface Turno {
  IDiaSemana: number; IOrdemTurno: number; HEntrada: string; HSaida: string; IbDiaFolga: boolean; IbFolgaDsr: boolean;
}
interface JornadaLista { id: number; sNome: string; iTipo: number; iDiasCiclo: number; ibAtivo: boolean; }

const DIAS = ["—", "Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const hhmm = (t: string) => (t ?? "").slice(0, 5);
const toTs = (t: string) => (t.length === 5 ? `${t}:00` : t);

const VAZIO = () => ({
  Id: 0, SNome: "", ITipo: 0, IDiasCiclo: 0, DtInicioCiclo: "",
  IbIntervaloPreAssinalado: false, FCargaSemanalMinutos: 0,
  IToleranciaEntradaMin: 5, IToleranciaSaidaMin: 5, IbAtivo: true,
  Dias: [] as Turno[],
});

export default function JornadasPage() {
  const [itens, setItens] = useState<JornadaLista[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState<any>(VAZIO());
  const [excluirId, setExcluirId] = useState<number | null>(null);

  const carregar = () => {
    setCarregando(true);
    apiClient.get<JornadaLista[]>("/api/rh/ponto/jornadas")
      .then((r) => setItens(r.data))
      .catch((e) => toast.error(e.message ?? "Falha ao carregar."))
      .finally(() => setCarregando(false));
  };
  useEffect(carregar, []);

  const novo = () => { setForm(VAZIO()); setDialog(true); };

  const editar = async (id: number) => {
    try {
      const r = await apiClient.get(`/api/rh/ponto/jornadas/${id}`);
      const j = r.data;
      setForm({
        Id: j.id, SNome: j.sNome, ITipo: j.iTipo, IDiasCiclo: j.iDiasCiclo,
        DtInicioCiclo: j.dtInicioCiclo ? j.dtInicioCiclo.slice(0, 10) : "",
        IbIntervaloPreAssinalado: j.ibIntervaloPreAssinalado, FCargaSemanalMinutos: j.fCargaSemanalMinutos,
        IToleranciaEntradaMin: j.iToleranciaEntradaMin, IToleranciaSaidaMin: j.iToleranciaSaidaMin, IbAtivo: j.ibAtivo,
        Dias: (j.dias ?? []).map((d: any) => ({
          IDiaSemana: d.iDiaSemana, IOrdemTurno: d.iOrdemTurno, HEntrada: hhmm(d.hEntrada), HSaida: hhmm(d.hSaida),
          IbDiaFolga: d.ibDiaFolga, IbFolgaDsr: d.ibFolgaDsr,
        })),
      });
      setDialog(true);
    } catch (e: any) { toast.error(e.message ?? "Falha ao abrir."); }
  };

  const addTurno = () => setForm((f: any) => ({
    ...f, Dias: [...f.Dias, { IDiaSemana: 2, IOrdemTurno: f.Dias.length + 1, HEntrada: "08:00", HSaida: "12:00", IbDiaFolga: false, IbFolgaDsr: false }],
  }));
  const setTurno = (i: number, patch: Partial<Turno>) => setForm((f: any) => ({
    ...f, Dias: f.Dias.map((t: Turno, idx: number) => (idx === i ? { ...t, ...patch } : t)),
  }));
  const rmTurno = (i: number) => setForm((f: any) => ({ ...f, Dias: f.Dias.filter((_: Turno, idx: number) => idx !== i) }));

  const salvar = async () => {
    if (!form.SNome.trim()) { toast.warning("Informe o nome da jornada."); return; }
    const payload = {
      ...form,
      DtInicioCiclo: form.IDiasCiclo > 0 && form.DtInicioCiclo ? form.DtInicioCiclo : null,
      Dias: form.Dias.map((t: Turno) => ({ ...t, HEntrada: toTs(t.HEntrada), HSaida: toTs(t.HSaida) })),
    };
    try {
      await apiClient.post("/api/rh/ponto/jornadas", payload);
      toast.success("Jornada salva.");
      setDialog(false); carregar();
    } catch (e: any) { toast.error(e.message ?? "Falha ao salvar."); }
  };

  const excluir = async () => {
    if (excluirId == null) return;
    try {
      await apiClient.delete(`/api/rh/ponto/jornadas/${excluirId}`);
      toast.success("Jornada excluída."); setExcluirId(null); carregar();
    } catch (e: any) { toast.error(e.message ?? "Falha ao excluir."); }
  };

  const ehCiclo = form.IDiasCiclo > 0;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Jornadas</h1>
        <Button onClick={novo}><Plus className="h-4 w-4 mr-2" />Nova jornada</Button>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Nome</TableHead><TableHead>Tipo</TableHead><TableHead className="w-24"></TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {carregando ? (
              <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground">Carregando…</TableCell></TableRow>
            ) : itens.length === 0 ? (
              <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground">Nenhuma jornada.</TableCell></TableRow>
            ) : itens.map((j) => (
              <TableRow key={j.id} className={!j.ibAtivo ? "opacity-50" : undefined}>
                <TableCell>{j.sNome}</TableCell>
                <TableCell>{j.iDiasCiclo > 0 ? <Badge variant="secondary">Ciclo {j.iDiasCiclo} dias</Badge> : "Semanal"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => editar(j.id)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => setExcluirId(j.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form.Id ? "Editar jornada" : "Nova jornada"}</DialogTitle></DialogHeader>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="space-y-1 col-span-2 sm:col-span-1">
              <Label>Nome</Label>
              <Input value={form.SNome} onChange={(e) => setForm({ ...form, SNome: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Modelo</Label>
              <Select value={ehCiclo ? "ciclo" : "semanal"} onValueChange={(v) => setForm({ ...form, IDiasCiclo: v === "ciclo" ? (form.IDiasCiclo || 2) : 0, ITipo: v === "ciclo" ? 1 : 0 })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="semanal">Semanal fixa</SelectItem>
                  <SelectItem value="ciclo">Ciclo (12x36…)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {ehCiclo && (
              <>
                <div className="space-y-1">
                  <Label>Dias do ciclo</Label>
                  <Input type="number" value={form.IDiasCiclo} onChange={(e) => setForm({ ...form, IDiasCiclo: Number(e.target.value) || 2 })} />
                </div>
                <div className="space-y-1">
                  <Label>Início do ciclo (âncora)</Label>
                  <Input type="date" value={form.DtInicioCiclo} onChange={(e) => setForm({ ...form, DtInicioCiclo: e.target.value })} />
                </div>
              </>
            )}
            <div className="space-y-1">
              <Label>Tolerância entrada (min)</Label>
              <Input type="number" value={form.IToleranciaEntradaMin} onChange={(e) => setForm({ ...form, IToleranciaEntradaMin: Number(e.target.value) || 0 })} />
            </div>
            <div className="space-y-1">
              <Label>Tolerância saída (min)</Label>
              <Input type="number" value={form.IToleranciaSaidaMin} onChange={(e) => setForm({ ...form, IToleranciaSaidaMin: Number(e.target.value) || 0 })} />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Switch checked={form.IbIntervaloPreAssinalado} onCheckedChange={(v) => setForm({ ...form, IbIntervaloPreAssinalado: v })} />
              <Label>Intervalo pré-assinalado</Label>
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Switch checked={form.IbAtivo} onCheckedChange={(v) => setForm({ ...form, IbAtivo: v })} />
              <Label>Ativa</Label>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-base">Turnos {ehCiclo ? "(posição no ciclo 1..N)" : "(dia da semana)"}</Label>
              <Button variant="outline" size="sm" onClick={addTurno}><Plus className="h-4 w-4 mr-1" />Turno</Button>
            </div>
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{ehCiclo ? "Posição" : "Dia"}</TableHead>
                    <TableHead>Ordem</TableHead>
                    <TableHead>Entrada</TableHead>
                    <TableHead>Saída</TableHead>
                    <TableHead>Folga</TableHead>
                    <TableHead>DSR</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {form.Dias.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground text-sm">Sem turnos — adicione.</TableCell></TableRow>
                  ) : form.Dias.map((t: Turno, i: number) => (
                    <TableRow key={i}>
                      <TableCell>
                        {ehCiclo ? (
                          <Input type="number" className="w-16" value={t.IDiaSemana} onChange={(e) => setTurno(i, { IDiaSemana: Number(e.target.value) || 1 })} />
                        ) : (
                          <Select value={String(t.IDiaSemana)} onValueChange={(v) => setTurno(i, { IDiaSemana: Number(v) })}>
                            <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                            <SelectContent>{[1, 2, 3, 4, 5, 6, 7].map((d) => <SelectItem key={d} value={String(d)}>{DIAS[d]}</SelectItem>)}</SelectContent>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell><Input type="number" className="w-16" value={t.IOrdemTurno} onChange={(e) => setTurno(i, { IOrdemTurno: Number(e.target.value) || 1 })} /></TableCell>
                      <TableCell><Input type="time" className="w-28" value={t.HEntrada} disabled={t.IbDiaFolga} onChange={(e) => setTurno(i, { HEntrada: e.target.value })} /></TableCell>
                      <TableCell><Input type="time" className="w-28" value={t.HSaida} disabled={t.IbDiaFolga} onChange={(e) => setTurno(i, { HSaida: e.target.value })} /></TableCell>
                      <TableCell><Switch checked={t.IbDiaFolga} onCheckedChange={(v) => setTurno(i, { IbDiaFolga: v })} /></TableCell>
                      <TableCell><Switch checked={t.IbFolgaDsr} disabled={!t.IbDiaFolga} onCheckedChange={(v) => setTurno(i, { IbFolgaDsr: v })} /></TableCell>
                      <TableCell><Button variant="ghost" size="icon" onClick={() => rmTurno(i)}><X className="h-4 w-4" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button onClick={salvar}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={excluirId != null} onOpenChange={(o) => !o && setExcluirId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir jornada?</AlertDialogTitle>
            <AlertDialogDescription>Os turnos também serão removidos.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={excluir}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
